/** Cinematic angle presets for keyframe "new view" generation. */
export const ANGLE_PRESETS = [
    {
        id: "orbit-left-30",
        label: "+30° left",
        camera: "Slight orbit: camera ~30° to the subject's left of the reference framing, same height and distance, medium shot.",
    },
    {
        id: "orbit-right-30",
        label: "+30° right",
        camera: "Slight orbit: camera ~30° to the subject's right of the reference framing, same height and distance, medium shot.",
    },
    {
        id: "three-quarter-left",
        label: "3/4 left",
        camera: "Three-quarter view from the subject's left, eye level, medium shot; face and body clearly readable.",
    },
    {
        id: "three-quarter-right",
        label: "3/4 right",
        camera: "Three-quarter view from the subject's right, eye level, medium shot; face and body clearly readable.",
    },
    {
        id: "left-profile",
        label: "Left profile",
        camera: "Left profile (subject's left), true side view, eye level, same subject scale as a portrait reference.",
    },
    {
        id: "right-profile",
        label: "Right profile",
        camera: "Right profile (subject's right), true side view, eye level, same subject scale as a portrait reference.",
    },
    {
        id: "front",
        label: "Front",
        camera: "Front view, facing camera straight-on, eye level, medium shot.",
    },
    {
        id: "reverse-180",
        label: "Reverse 180°",
        camera: "Reverse angle: camera ~180° opposite the reference viewpoint, looking back toward where the reference camera stood.",
    },
    {
        id: "ots-left",
        label: "OTS left",
        camera: "Over-the-shoulder shot from behind the near subject's left shoulder, looking at the far subject or scene, medium close-up.",
    },
    {
        id: "ots-right",
        label: "OTS right",
        camera: "Over-the-shoulder shot from behind the near subject's right shoulder, looking at the far subject or scene, medium close-up.",
    },
    {
        id: "low-angle",
        label: "Low angle",
        camera: "Low-angle shot, camera below eye level looking up at the subject, medium shot.",
    },
    {
        id: "high-angle",
        label: "High angle",
        camera: "High-angle shot, camera above looking down on the subject, medium-wide shot.",
    },
    {
        id: "wide",
        label: "Wide",
        camera: "Wide establishing shot, same place and subjects, more environment visible, camera pulled back.",
    },
    {
        id: "close-up",
        label: "Close-up",
        camera: "Close-up framing on the main subject (face or focal detail), same lighting and identity as the reference.",
    },
];
export const DEFAULT_VIEW_CAMERA = "Slight orbit: camera ~30° to the subject's left of the reference framing, same height and distance, medium shot.";
/**
 * Nano Banana–style Keep / Camera / Response prompt for novel views from a reference still.
 * Lead with CAMERA so spatial instructions win over identity pressure.
 */
export function buildNewViewPrompt(cameraInstruction) {
    const camera = cameraInstruction.trim();
    return [
        `CAMERA: ${camera}`,
        "Keep the same people, wardrobe, place, props, time of day, and lighting quality as the reference image.",
        "Only change the camera position and framing; reveal whatever is newly visible from that viewpoint.",
        "Do not copy the reference framing or composition.",
    ].join("\n");
}
export const TURNAROUND_WHITE_BG = "Plain pure white seamless studio background, even soft lighting, full body or three-quarter character reference still, clean edges, no props, no text, no watermark.";
export const TURNAROUND_VIEW_STEPS = [
    {
        id: "front",
        label: "front",
        view: "Front view, facing camera straight-on, clear face and full wardrobe.",
    },
    {
        id: "back",
        label: "back",
        view: "Back view, facing away from camera, same pose scale and framing as the front reference.",
    },
    {
        id: "left",
        label: "left profile",
        view: "Left profile (subject's left), side view, same pose scale and framing as the other references.",
    },
    {
        id: "right",
        label: "right profile",
        view: "Right profile (subject's right), side view, same pose scale and framing as the other references.",
    },
    {
        id: "back-final",
        label: "final back",
        view: "Final refined back view, facing away from camera, perfectly matching identity and wardrobe of all reference images, same pose scale and framing.",
    },
];
export function buildTurnaroundPrompt(subjectDescription, view) {
    return [
        `Character turnaround reference sheet still.`,
        `Subject: ${subjectDescription}`,
        view,
        `Keep the exact same person, face, hair, skin, body, and wardrobe as any reference images.`,
        TURNAROUND_WHITE_BG,
    ].join(" ");
}
export const PLACE_VIEW_STEPS = [
    {
        id: "establishing",
        label: "establishing",
        view: "Wide establishing shot of the place, empty of people and animals, clear landmark layout, children's-book friendly.",
    },
    {
        id: "alternate",
        label: "alternate angle",
        view: "Alternate camera angle of the same place, slightly different viewpoint, same time of day and lighting, still empty of characters.",
    },
];
export function buildPlacePrompt(subjectDescription, view) {
    return [
        `Environment / place reference still for a children's video.`,
        `Place: ${subjectDescription}`,
        view,
        `No characters, no people, no animals, no text, no watermark.`,
        `Keep the same location identity, architecture, palette, and atmosphere as any reference images.`,
    ].join(" ");
}
export function resolveAngleCamera(angleId, cameraInstruction) {
    if (cameraInstruction?.trim())
        return cameraInstruction.trim();
    if (angleId) {
        const preset = ANGLE_PRESETS.find((p) => p.id === angleId);
        if (preset)
            return preset.camera;
    }
    return DEFAULT_VIEW_CAMERA;
}
//# sourceMappingURL=angles.js.map